import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { Post, PostSchema } from "../../post/postSchema";
import { Campaign, CampaignSchema } from "src/api/client-api/useCampaign.ts";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";

export const getOneActionPlanKey = "getOneActionPlan" as const;

type Options = {
    actionPlanId: (string | number) | undefined;
    scoresQuery?: OlapScoresQuery;
};
export const useGetOneActionPlan = (options: Options) => {
    const { actionPlanId, scoresQuery } = options;

    const scoresQueryKey = scoresQuery ? JSON.stringify(scoresQuery) : undefined;

    const query = useQuery({
        enabled: actionPlanId !== undefined,
        queryKey: [getOneActionPlanKey, actionPlanId, scoresQueryKey],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const url = `/api/action_plans/${actionPlanId}`;

            const response = await loggedInApiCall.get(url, {
                params: scoresQuery,
            });
            return response;
        },
        select: (resp) => {
            const data = resp.data;
            const validatedData = zodParseWithLog(data, GetOneActionPlanSchema, getOneActionPlanKey);
            return validatedData;
        },
    });

    return query;
};

export type ActionPlanSsqPostSummary = Pick<Post, "id" | "campaign" | "olapScores" | "isLocked" | "ssqVersionId">;

export type OneActionPlan = ActionPlan & {
    simulationSsqPosts: ActionPlanSsqPostSummary[];
};

export type SsqPost = ActionPlanSsqPostSummary;

const SsqPostSchema: z.ZodType<SsqPost> = PostSchema.pick({
    id: true,
    campaign: true,
    olapScores: true,
    isLocked: true,
    ssqVersionId: true,
});

export type ActionPlanSsqPosts = {
    id: string;
    ssqPost: SsqPost;
};

const ActionPlanSsqPostsSchema: z.ZodType<ActionPlanSsqPosts> = z.object({
    id: z.string(),
    ssqPost: SsqPostSchema,
});

export type ActionPlanScoreTargets = {
    "@id": string;
    id: string;
    scoreId: number;
    variationId?: number;
    simulationVaryPercentage?: number;
    simulationVaryAbsolute?: number;
};
const ActionPlanScoreTargetsSchema: z.ZodType<ActionPlanScoreTargets> = z.object({
    "@id": z.string(),
    id: z.string(),
    scoreId: z.number(),
    variationId: z.number().optional(),
    simulationVaryPercentage: z.number().optional(),
    simulationVaryAbsolute: z.number().optional(),
});

export type ActionPlan = {
    "@id": IRI;
    id: string;
    name: string;
    description?: string;
    simulationVaryPercentage?: number;
    actionPlanSsqPosts: ActionPlanSsqPosts[];
    actionPlanScoreTargets: ActionPlanScoreTargets[];
    startSsqPost: SsqPost;
    endCampaign: Campaign;
    staleStatus?: "answer_changed" | "version_migrated" | null;
};
const ExtendableActionPlanSchema = z.object({
    "@id": z.string(),
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    simulationVaryPercentage: z.number().optional(),
    actionPlanSsqPosts: ActionPlanSsqPostsSchema.array(),
    actionPlanScoreTargets: ActionPlanScoreTargetsSchema.array(),

    startSsqPost: SsqPostSchema,
    endCampaign: CampaignSchema,
    staleStatus: z.enum(["answer_changed", "version_migrated"]).nullish(),
});

const GetOneActionPlanSchema: z.ZodType<OneActionPlan> = ExtendableActionPlanSchema.extend({
    simulationSsqPosts: PostSchema.pick({
        id: true,
        olapScores: true,
        campaign: true,
        isLocked: true,
        ssqVersionId: true,
    }).array(),
});
